import React, { useState, useRef } from "react";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Paper,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import Logo from "../../assets/logos/qctech.jpg";

const GST_RATES = [0, 5, 12, 18, 28];
const CURRENCY_OPTIONS = [
  { code: "INR", symbol: "₹", label: "Rupee", name: "Rupees" },
  { code: "AED", symbol: "AED", label: "Dirham", name: "AED" },
  { code: "USD", symbol: "$", label: "Dollar", name: "USD" },
];

export default function Qctech() {
  // Supplier Details
  const supplierDetails = {
    name: "Quantum Culture Technologies",
    addressline1: "115, Siva Complex",
    addressline2: "Palladam Road, Pollachi - 642 001",
    gstin: "33ALIPH9477M2ZF",
    email: "hello@qctech.in",
    phone: "+91 73730 89996",
    bankAccName: "Quantum Culture Technologies",
    bankName: "TamilNadu Mercantile Bank",
    bankBranch: "Pollachi",
    bankIFSC: "TMBL0000090",
    bankAccNo: "090150050801101",
    logo: Logo,
  };

  // Invoice & recipient state
  const [recipientName, setRecipientName] = useState("");
  const [recipientAddress1, setRecipientAddress1] = useState("");
  const [recipientAddress2, setRecipientAddress2] = useState("");
  const [recipientContactDetails, setRecipientContactDetails] = useState("");
  const [recipientGSTIN, setRecipientGSTIN] = useState("");
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [deliveryAddName, setDeliveryAddName] = useState("");
  const [deliveryAddress1, setDeliveryAddress1] = useState("");
  const [deliveryAddress2, setDeliveryAddress2] = useState("");
  const [deliveryContactDetails, setDeliveryContactDetails] = useState("");
  const [deliveryGSTIN, setDeliveryGSTIN] = useState("");

  // Toggles & rates
  const [applyGST, setApplyGST] = useState(false);
  const [gstType, setGstType] = useState("");
  const [gstRate, setGstRate] = useState(0);
  const [discountChecked, setDiscountChecked] = useState(false);
  const [discountRate, setDiscountRate] = useState(0);
  const [roundOff, setRoundOff] = useState(false);

  // Currency & extra/service charge
  const [currency, setCurrency] = useState("INR");
  const [extraType, setExtraType] = useState("amount"); // "amount" or "percent"
  const [extraValue, setExtraValue] = useState(0);

  // Line items
  const initialItem = { description: "", hsn: "", quantity: 1, unitPrice: 0 };
  const [items, setItems] = useState([{ ...initialItem }]);
  const addItem = () => setItems((prev) => [...prev, { ...initialItem }]);
  const updateItem = (idx, field, value) => {
    const arr = [...items];
    arr[idx][field] = value;
    setItems(arr);
  };

  // Totals calculation
  const subtotal = items.reduce((sum, i) => sum + i.quantity * i.unitPrice, 0);
  const discountAmt = discountChecked ? (subtotal * discountRate) / 100 : 0;
  const taxableVal = subtotal - discountAmt;

  let cgstAmt = 0,
    sgstAmt = 0,
    igstAmt = 0,
    tax = 0;
  if (applyGST && gstRate) {
    if (gstType === "IGST") {
      igstAmt = (taxableVal * gstRate) / 100;
    } else if (gstType === "CGST_SGST") {
      cgstAmt = (taxableVal * gstRate) / 200;
      sgstAmt = cgstAmt;
    } else if (gstType === "TAX") {
      tax = (taxableVal * gstRate) / 100;
    }
  }
  const gstTotal = cgstAmt + sgstAmt + igstAmt + tax;
  const intermediateTotal = taxableVal + gstTotal;
  const extraAmt =
    extraType === "percent"
      ? (intermediateTotal * extraValue) / 100
      : extraValue;
  const totalBeforeRound = intermediateTotal + extraAmt;
  const finalTotal = roundOff ? Math.round(totalBeforeRound) : totalBeforeRound;
  const roundOffAmt = finalTotal - totalBeforeRound;
  const currencySymbol =
    CURRENCY_OPTIONS.find((c) => c.code === currency)?.symbol || "";
  const currencyLabel =
    CURRENCY_OPTIONS.find((c) => c.code === currency)?.name || "";
  const amountInWords = convertToWords(Math.round(finalTotal));

  // PDF generation
  const printRef = useRef();
  const handleSubmit = async (e) => {
    e.preventDefault();
    const canvas = await html2canvas(printRef.current);
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "pt", "a4");
    const w = pdf.internal.pageSize.getWidth();
    const h = (canvas.height * w) / canvas.width;
    pdf.addImage(img, "PNG", 0, 0, w, h);
    pdf.save(`Invoice_${invoiceNumber || "DOC"}.pdf`);
  };

  // Helper: number → words
  function convertToWords(num) {
    const single = [
      "",
      "One",
      "Two",
      "Three",
      "Four",
      "Five",
      "Six",
      "Seven",
      "Eight",
      "Nine",
    ];
    const teens = [
      "Ten",
      "Eleven",
      "Twelve",
      "Thirteen",
      "Fourteen",
      "Fifteen",
      "Sixteen",
      "Seventeen",
      "Eighteen",
      "Nineteen",
    ];
    const tens = [
      "",
      "",
      "Twenty",
      "Thirty",
      "Forty",
      "Fifty",
      "Sixty",
      "Seventy",
      "Eighty",
      "Ninety",
    ];
    if (num === 0) return "Zero";
    if (num < 10) return single[num];
    if (num < 20) return teens[num - 10];
    if (num < 100)
      return (
        tens[Math.floor(num / 10)] + (num % 10 ? " " + single[num % 10] : "")
      );
    if (num < 1000)
      return (
        single[Math.floor(num / 100)] +
        " Hundred" +
        (num % 100 ? " and " + convertToWords(num % 100) : "")
      );
    if (num < 100000)
      return (
        convertToWords(Math.floor(num / 1000)) +
        " Thousand" +
        (num % 1000 ? " " + convertToWords(num % 1000) : "")
      );
    if (num < 10000000)
      return (
        convertToWords(Math.floor(num / 100000)) +
        " Lakh" +
        (num % 100000 ? " " + convertToWords(num % 100000) : "")
      );
    return (
      convertToWords(Math.floor(num / 10000000)) +
      " Crore" +
      (num % 10000000 ? " " + convertToWords(num % 10000000) : "")
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Invoice Preview */}
      <Paper
        ref={printRef}
        elevation={0}
        sx={{
          width: "210mm",
          minHeight: "297mm",
          p: 3,
          border: "1px solid #ccc",
          m: "0 auto",
          bgcolor: "#fff",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
            pb: 1,
            borderBottom: "2px solid #1EA7C9",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box
              component="img"
              src={supplierDetails.logo}
              alt="Logo"
              sx={{ height: 80, mr: 2 }}
            />
            <Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", color: "#1EA7C9" }}
              >
                {supplierDetails.name}
              </Typography>
              <Typography variant="body2">
                {supplierDetails.addressline1}
                <br />
                {supplierDetails.addressline2}
              </Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>GSTIN:</strong> {supplierDetails.gstin}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ textAlign: "right", mt: 6 }}>
            <Typography variant="body2">
              <strong>Phone:</strong> {supplierDetails.phone}
            </Typography>
            <Typography variant="body2">
              <strong>Email:</strong> {supplierDetails.email}
            </Typography>
          </Box>
        </Box>

        {/* Title & Info */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="h4"
            align="center"
            sx={{ fontWeight: "bold", color: "#1EA7C9" }}
          >
            TAX INVOICE
          </Typography>
          <Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Invoice No:
              </Typography>
              <Typography variant="body2">{invoiceNumber}</Typography>
            </Box>
            <Box
              sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Invoice Date:
              </Typography>
              <Typography variant="body2">{invoiceDate}</Typography>
            </Box>
          </Box>
        </Box>

        {/* Bill To & Delivery To */}
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Box sx={{ width: "48%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: "400" }}>
                  Bill To:
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "400", whiteSpace: "nowrap" }}
                >
                  <strong>{recipientName}</strong>
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {recipientAddress1}
                <br />
                {recipientAddress2}
                <br />
                {recipientContactDetails}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bolder" }}>
                {recipientGSTIN}
              </Typography>
            </Paper>
          </Box>
          <Box sx={{ width: "48%" }}>
            <Paper
              elevation={0}
              sx={{
                p: 2,
                bgcolor: "#f8f9fa",
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                }}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: "400" }}>
                  Delivery To:
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: "400", whiteSpace: "nowrap" }}
                >
                  <strong>{deliveryAddName}</strong>
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                {deliveryAddress1}
                <br />
                {deliveryAddress2}
                <br />
                {deliveryContactDetails}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: "bolder" }}>
                {deliveryGSTIN}
              </Typography>
            </Paper>
          </Box>
        </Box>

        {/* Item Details */}
        <Box sx={{ mb: 1 }}>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
              mb: 1,
              color: "#1EA7C9",
            }}
          >
            ITEM DETAILS
          </Typography>
          <Box
            sx={{
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            {/* Header */}
            <Box
              sx={{
                display: "flex",
                bgcolor: "#f0f2f5",
                borderBottom: "1px solid #e0e0e0",
                p: 1.5,
              }}
            >
              <Box sx={{ flex: 3, fontWeight: "bold", fontSize: "0.875rem" }}>
                Description
              </Box>
              <Box sx={{ flex: 1, fontWeight: "bold", fontSize: "0.875rem" }}>
                HSN/SAC
              </Box>
              {/* <Box
                sx={{
                  width: 100,
                  textAlign: "right",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                }}
              >
                Unit Price
              </Box> */}
              <Box
                sx={{
                  width: 120,
                  textAlign: "right",
                  fontWeight: "bold",
                  fontSize: "0.875rem",
                }}
              >
                Amount
              </Box>
            </Box>

            {/* Regular Items */}
            {items.map((it, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  p: 1.5,
                  borderBottom:
                    idx < items.length - 1 ? "1px solid #e0e0e0" : "none",
                  bgcolor: idx % 2 === 0 ? "#fff" : "#fafafa",
                }}
              >
                <Box sx={{ flex: 3, fontSize: "0.875rem" }}>
                  {it.description}
                </Box>
                <Box sx={{ flex: 1, fontSize: "0.875rem" }}>{it.hsn}</Box>
                {/* <Box
                  sx={{
                    width: 100,
                    textAlign: "right",
                    fontSize: "0.875rem",
                  }}
                >
                  {currencySymbol}
                  {it.unitPrice.toFixed(2)}
                </Box> */}
                <Box
                  sx={{
                    width: 120,
                    textAlign: "right",
                    fontSize: "0.875rem",
                  }}
                >
                  {currencySymbol}
                  {(it.quantity * it.unitPrice).toFixed(2)}
                </Box>
              </Box>
            ))}

            {/* Service/Extra Charge Row */}
            {extraValue > 0 && (
              <Box
                sx={{
                  display: "flex",
                  p: 1.5,
                  borderBottom: "1px solid #e0e0e0",
                  bgcolor: items.length % 2 === 0 ? "#fff" : "#fafafa",
                }}
              >
                <Box sx={{ flex: 3, fontSize: "0.875rem" }}>
                  Service Charge
                  {extraType === "percent" ? ` (${extraValue}%)` : ""}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Box
                  sx={{
                    width: 100,
                    textAlign: "right",
                    fontSize: "0.875rem",
                  }}
                >
                  {currencySymbol}
                  {extraAmt.toFixed(2)}
                </Box>
                <Box
                  sx={{
                    width: 120,
                    textAlign: "right",
                    fontSize: "0.875rem",
                  }}
                >
                  {currencySymbol}
                  {extraAmt.toFixed(2)}
                </Box>
              </Box>
            )}
          </Box>
        </Box>

        {/* Summary Section */}
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
          <Box
            sx={{
              width: 350,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                p: 1.5,
                borderBottom: "1px solid #e0e0e0",
                bgcolor: "#f8f9fa",
              }}
            >
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">
                {currencySymbol}
                {subtotal.toFixed(2)}
              </Typography>
            </Box>
            {discountChecked && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2">
                  Discount ({discountRate}%)
                </Typography>
                <Typography variant="body2">
                  -{currencySymbol}
                  {discountAmt.toFixed(2)}
                </Typography>
              </Box>
            )}
            {applyGST && (
              <>
                {gstType === "IGST" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2">IGST ({gstRate}%)</Typography>
                    <Typography variant="body2">
                      {currencySymbol}
                      {igstAmt.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {gstType === "TAX" && (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      p: 1.5,
                      borderBottom: "1px solid #e0e0e0",
                    }}
                  >
                    <Typography variant="body2">Tax ({gstRate}%)</Typography>
                    <Typography variant="body2">
                      {currencySymbol}
                      {tax.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {gstType === "CGST_SGST" && (
                  <>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography variant="body2">
                        CGST ({gstRate / 2}%)
                      </Typography>
                      <Typography variant="body2">
                        {currencySymbol}
                        {cgstAmt.toFixed(2)}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                      }}
                    >
                      <Typography variant="body2">
                        SGST ({gstRate / 2}%)
                      </Typography>
                      <Typography variant="body2">
                        {currencySymbol}
                        {sgstAmt.toFixed(2)}
                      </Typography>
                    </Box>
                  </>
                )}
              </>
            )}
            {roundOff && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  p: 1.5,
                  borderBottom: "1px solid #e0e0e0",
                }}
              >
                <Typography variant="body2">Round Off Adjustment</Typography>
                <Typography variant="body2">
                  {currencySymbol}
                  {roundOffAmt.toFixed(2)}
                </Typography>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                p: 1.5,
                bgcolor: "#1EA7C9",
                color: "#fff",
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                TOTAL
              </Typography>
              <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                {currencySymbol}
                {finalTotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Amount in Words */}
        <Box
          sx={{
            p: 2,
            bgcolor: "#f8f9fa",
            border: "1px solid #e0e0e0",
            borderRadius: 1,
            mb: 0.5,
          }}
        >
          <Typography variant="body2">
            <strong>Amount in Words:</strong> {amountInWords} {currencyLabel}{" "}
            {currencyLabel === "Rupees" ? "Only" : ""}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mb: 1,
          }}
        >
          {/* Payment Details */}
          <Box sx={{ mb: 0.5 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                mb: 1,
                color: "#1EA7C9",
              }}
            >
              PAYMENT DETAILS
            </Typography>
            <Box
              sx={{
                p: 1,
                border: "1px solid #e0e0e0",
                borderRadius: 1,
              }}
            >
              <Typography sx={{ mb: 0.5 }}>
                <strong>Bank Acc Name:</strong> {supplierDetails.bankAccName}
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  gap: 0.2,
                  justifyContent: "space-between",
                  flexDirection: "column",
                }}
              >
                <Typography>
                  <strong>Bank Name:</strong> {supplierDetails.bankName}
                </Typography>
                <Typography>
                  <strong>Acc No:</strong> {supplierDetails.bankAccNo}
                </Typography>
                <Typography>
                  <strong>IFSC:</strong> {supplierDetails.bankIFSC}
                </Typography>
                <Typography>
                  <strong>Branch:</strong> {supplierDetails.bankBranch}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              mt: 2,
            }}
          >
            <Typography variant="body2">Authorized Signatory</Typography>

            <Typography variant="body2" sx={{ fontWeight: "bold" }}>
              {supplierDetails.name}
            </Typography>
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Thank you for your business!
          </Typography>
        </Box>
      </Paper>

      {/* Form Inputs */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="h6">Enter Details</Typography>
        {/* Invoice Info */}
        <Box sx={{ display: "flex", gap: 2 }}>
          <TextField
            label="Invoice No"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Invoice Date"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={invoiceDate}
            onChange={(e) => setInvoiceDate(e.target.value)}
            required
            fullWidth
          />
          <TextField
            label="Place of Service"
            value={placeOfSupply}
            onChange={(e) => setPlaceOfSupply(e.target.value)}
            required
            fullWidth
          />
        </Box>

        {/* Addresses */}
        <Typography
          sx={{
            textAlign: "center",
            fontSize: 20,
            fontWeight: "bolder",
          }}
        >
          Address
        </Typography>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <TextField
              label="Recipient Name"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Recipient GSTIN"
              value={recipientGSTIN}
              onChange={(e) => setRecipientGSTIN(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Recipient Contact"
              value={recipientContactDetails}
              onChange={(e) => setRecipientContactDetails(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Recipient Address 1"
              value={recipientAddress1}
              onChange={(e) => setRecipientAddress1(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Recipient Address 2"
              value={recipientAddress2}
              onChange={(e) => setRecipientAddress2(e.target.value)}
              required
              fullWidth
            />
          </Box>
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <TextField
              label="Delivery Name"
              value={deliveryAddName}
              onChange={(e) => setDeliveryAddName(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Delivery GSTIN"
              value={deliveryGSTIN}
              onChange={(e) => setDeliveryGSTIN(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Delivery Contact"
              value={deliveryContactDetails}
              onChange={(e) => setDeliveryContactDetails(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Delivery Address 1"
              value={deliveryAddress1}
              onChange={(e) => setDeliveryAddress1(e.target.value)}
              required
              fullWidth
            />
            <TextField
              label="Delivery Address 2"
              value={deliveryAddress2}
              onChange={(e) => setDeliveryAddress2(e.target.value)}
              required
              fullWidth
            />
          </Box>
        </Box>

        {/* Items Input */}
        <Typography variant="subtitle1">Items</Typography>
        {items.map((item, idx) => (
          <Box
            key={idx}
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            <TextField
              label="Description"
              value={item.description}
              onChange={(e) => updateItem(idx, "description", e.target.value)}
              sx={{ flex: 2 }}
              required
            />
            <TextField
              label="HSN/SAC"
              value={item.hsn}
              onChange={(e) => updateItem(idx, "hsn", e.target.value)}
              sx={{ flex: 1 }}
              required
            />
            <TextField
              label="Unit Price"
              type="number"
              value={item.unitPrice}
              onChange={(e) =>
                updateItem(idx, "unitPrice", Number(e.target.value))
              }
              sx={{ width: 120 }}
              required
            />
          </Box>
        ))}
        <Button
          startIcon={<AddCircleOutlineIcon />}
          onClick={addItem}
          sx={{ alignSelf: "flex-start" }}
        >
          Add Item
        </Button>

        {/* Options */}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            alignItems: "center",
          }}
        >
          {/* GST */}
          <FormControlLabel
            control={
              <Checkbox
                checked={applyGST}
                onChange={(e) => setApplyGST(e.target.checked)}
              />
            }
            label="Apply TAX"
          />
          {applyGST && (
            <>
              <FormControl sx={{ minWidth: 140 }}>
                <InputLabel>Tax Type</InputLabel>
                <Select
                  value={gstType}
                  onChange={(e) => setGstType(e.target.value)}
                  label="Tax Type"
                  required
                >
                  <MenuItem value="TAX">TAX</MenuItem>
                  <MenuItem value="IGST">IGST</MenuItem>
                  <MenuItem value="CGST_SGST">CGST + SGST</MenuItem>
                </Select>
              </FormControl>
              {gstType === "TAX" ? (
                <TextField
                  label="Tax %"
                  type="number"
                  value={gstRate}
                  onChange={(e) => setGstRate(Number(e.target.value))}
                  sx={{ width: 120 }}
                  required
                />
              ) : (
                <FormControl sx={{ minWidth: 100 }}>
                  <InputLabel>Rate (%)</InputLabel>
                  <Select
                    value={gstRate}
                    onChange={(e) => setGstRate(Number(e.target.value))}
                    label="Rate (%)"
                    required
                  >
                    {GST_RATES.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}%
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </>
          )}

          {/* Discount */}
          <FormControlLabel
            control={
              <Checkbox
                checked={discountChecked}
                onChange={(e) => setDiscountChecked(e.target.checked)}
              />
            }
            label="Apply Discount"
          />
          {discountChecked && (
            <TextField
              label="Discount %"
              type="number"
              value={discountRate}
              onChange={(e) => setDiscountRate(Number(e.target.value))}
              sx={{ width: 120 }}
              required
            />
          )}

          {/* Currency Selector */}
          <FormControl sx={{ minWidth: 140 }}>
            <InputLabel>Currency</InputLabel>
            <Select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              label="Currency"
            >
              {CURRENCY_OPTIONS.map((c) => (
                <MenuItem key={c.code} value={c.code}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Round Off */}
          <FormControlLabel
            control={
              <Checkbox
                checked={roundOff}
                onChange={(e) => setRoundOff(e.target.checked)}
              />
            }
            label="Round Off Total"
          />
        </Box>

        <Button variant="contained" type="submit" sx={{ mt: 2 }}>
          Download Invoice
        </Button>
      </Box>
    </Container>
  );
}
